import { useEffect, useState } from "react";
import { Plus, Power } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { getGames, createGame, deactivateGame } from "@/lib/api";
import { VISIBLE_TIPOS, TIPO_LABEL, isVisibleTipo } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const DEFAULT_PARAMS = {
  triple: '{"estados":["Amazonas","Anzoátegui","Apure","Aragua","Barinas","Bolívar","Carabobo","Cojedes","Delta Amacuro","Falcón","Guárico","Lara","Mérida","Miranda","Monagas","Nueva Esparta","Portuguesa","Sucre","Táchira","Trujillo","Yaracuy","Zulia","La Guaira","Distrito Capital"]}',
  animalitos: '{}',
  lotto: '{"n":49,"k":6}',
  rifas: '{"total_boletos":1000,"ganadores":3}',
  raspaditos: '{"tabla":[{"premio":"Sin premio","peso":800},{"premio":"Bs. 50","peso":40},{"premio":"JACKPOT","peso":1}]}',
};

export default function Games() {
  const { user } = useAuth();
  const canWrite = user?.rol === "admin" || user?.rol === "operador";
  const isAdmin = user?.rol === "admin";
  const [games, setGames] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ nombre: "", tipo: "triple", descripcion: "", parametros: DEFAULT_PARAMS.triple });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const load = () => getGames().then(setGames).catch(() => {});
  useEffect(() => { load(); }, []);

  const onTipo = (v) => setForm((f) => ({ ...f, tipo: v, parametros: DEFAULT_PARAMS[v] }));

  const onCreate = async () => {
    try {
      await createGame({ nombre: form.nombre, tipo: form.tipo, descripcion: form.descripcion, parametros: JSON.parse(form.parametros || "{}") });
      toast.success("Juego creado");
      setOpen(false);
      setForm({ nombre: "", tipo: "triple", descripcion: "", parametros: DEFAULT_PARAMS.triple });
      load();
    } catch {
      toast.error("Revisa los datos (parámetros deben ser JSON válido)");
    }
  };

  const onDeactivate = async (id) => {
    await deactivateGame(id);
    toast.success("Juego desactivado");
    load();
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <div><h1 className="font-display text-2xl font-bold">Juegos</h1><p className="text-sm text-muted-foreground">Configura las plantillas de sorteo.</p></div>
        {canWrite && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="gap-2" data-testid="games-new-button"><Plus className="h-4 w-4" /> Nuevo juego</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Nuevo juego</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div><Label>Nombre</Label><Input value={form.nombre} onChange={(e) => set("nombre", e.target.value)} data-testid="games-form-nombre" /></div>
              <div><Label>Tipo</Label>
                <Select value={form.tipo} onValueChange={onTipo}><SelectTrigger data-testid="games-form-tipo"><SelectValue /></SelectTrigger>
                  <SelectContent>{VISIBLE_TIPOS.map((t) => <SelectItem key={t} value={t}>{TIPO_LABEL[t]}</SelectItem>)}</SelectContent></Select></div>
              <div><Label>Descripción</Label><Input value={form.descripcion} onChange={(e) => set("descripcion", e.target.value)} /></div>
              <div><Label>Parámetros (JSON)</Label><Textarea rows={4} className="font-mono text-xs" value={form.parametros} onChange={(e) => set("parametros", e.target.value)} data-testid="games-form-parametros" /></div>
            </div>
            <DialogFooter><Button onClick={onCreate} disabled={!form.nombre} data-testid="games-form-submit">Crear juego</Button></DialogFooter>
          </DialogContent>
        </Dialog>
        )}
      </div>

      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {games.filter((g) => isVisibleTipo(g.tipo)).map((g) => (
          <Card key={g.id} data-theme={g.tipo} className="border-border">
            <div className="h-1.5" style={{ background: "hsl(var(--game-accent))" }} />
            <CardContent className="p-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-display text-lg font-bold">{g.nombre}</p>
                  <Badge variant="outline" className="mt-1">{TIPO_LABEL[g.tipo]}</Badge>
                </div>
                {g.activo ? <Badge className="bg-emerald-100 text-emerald-800">Activo</Badge> : <Badge variant="secondary">Inactivo</Badge>}
              </div>
              <p className="mt-2 text-sm text-muted-foreground">{g.descripcion}</p>
              <pre className="mt-3 overflow-x-auto rounded-md bg-muted/50 p-2 font-mono text-[11px]">{JSON.stringify(g.parametros)}</pre>
              {isAdmin && g.activo && <Button variant="outline" size="sm" className="mt-3 gap-1.5" onClick={() => onDeactivate(g.id)}><Power className="h-3.5 w-3.5" /> Desactivar</Button>}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
